import { cable } from "@hotwired/turbo-rails";

// The cable keeps the presenter's windows and phone on one board, and a
// colleague's copy when it follows; the BroadcastChannel opened on
// connect does the same for one laptop's windows without it. And the
// presenter's clock, and the phone kept awake.

export default {
  subscribe() {
    this.sender = Math.random().toString(36).slice(2);
    const controller = this;
    // Without the cable the BroadcastChannel still keeps one laptop's
    // windows together.
    this.subscription = cable
      .subscribeTo(
        { channel: "DeckRemoteChannel", report_id: this.reportIdValue },
        {
          received: (data) => this.received(data),
          // The phone says it is here, so the laptop can put the QR away.
          connected() {
            if (controller.remoteValue) this.send({ joined: true, sender: controller.sender });
          },
        },
      )
      .catch(() => null);
  },

  send(message) {
    this.subscription?.then((subscription) => subscription?.send({ ...message, sender: this.sender }));
  },

  // The room's screen is the window that is neither the presenter's nor
  // the phone: it is the one that goes full screen when the phone asks.
  get room() {
    return !this.presenterValue && !this.remoteValue;
  },

  received(data) {
    if (data.sender && data.sender === this.sender) return;
    // A note written in another window. One being written here is this
    // window's to keep.
    if (data.note?.slug && data.note.slug !== this.editingSlug) {
      this.notesBySlug[data.note.slug] = data.note.text || "";
      this.renderNotes();
    }
    // The presenter's own windows and phone move together. Someone else's
    // page turn moves this copy only when it follows the presenter, and
    // never its full screen or its menus.
    const own = data.user === undefined || data.user === this.userValue;
    // The boards were chosen again: a hidden board goes at once; a new
    // order is the deck drawn again, in the presenter's own windows.
    if (data.selection) {
      if (data.selection.reorder && own) this.reloadHere();
      else this.applyHidden(data.selection.hidden || []);
    }
    const turned = this.indexFor(data);
    const index = turned === undefined ? undefined : this.nearestVisible(turned);
    if ((own || this.followOthers) && index !== undefined && index !== this.indexValue) {
      this.showSlide(index, true, false);
    }
    if (!own) return;
    // The phone has scanned the QR: the menu that shows it closes, and the
    // room's screen tells the phone whether it is full screen.
    if (data.joined) {
      this.closeMenus();
      this.announceScreen();
    }
    if (typeof data.fullscreen === "boolean" && this.room) this.setScreen(data.fullscreen);
    if (typeof data.turned === "boolean") this.turnTo(data);
    if (typeof data.screen === "boolean" && this.hasScreenButtonTarget) {
      this.screenButtonTarget.setAttribute("aria-pressed", String(data.screen));
    }
  },

  // A phone that locks halfway through the meeting is a remote that stops.
  async keepAwake() {
    if (!("wakeLock" in navigator)) return;
    try {
      this.wakeLock = await navigator.wakeLock.request("screen");
    } catch {
      // Refused (low battery, or the page is not on show): the phone locks
      // as it would anyway.
    }
  },

  // The time of day and, on the phone, how long the talk has run since the
  // first page turned.
  startClock() {
    const pad = (n) => String(n).padStart(2, "0");
    const tick = () => {
      if (this.hasClockTarget) this.clockTarget.textContent = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      if (this.hasElapsedTarget && this.startedAt) {
        const seconds = Math.floor((Date.now() - this.startedAt) / 1000);
        const hours = Math.floor(seconds / 3600);
        const clock = `${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}`;
        this.elapsedTarget.textContent = hours ? `${hours}:${clock}` : clock;
      }
    };
    tick();
    this.clockTimer = setInterval(tick, this.hasElapsedTarget ? 1000 : 15000);
  },

  // Follow the presenter: a choice of this viewer's, kept in this browser
  // for every deck. Off unless switched on.
  toggleFollow() {
    this.followOthers = !this.followOthers;
    try {
      localStorage.setItem("carttype.deck.follow", this.followOthers ? "1" : "0");
    } catch {
      // No storage (a private window): the choice lasts until the page closes.
    }
    this.renderFollow();
  },

  readFollow() {
    try {
      return localStorage.getItem("carttype.deck.follow") === "1";
    } catch {
      return false;
    }
  },

  renderFollow() {
    if (!this.hasFollowButtonTarget) return;
    this.followButtonTarget.setAttribute("aria-pressed", String(this.followOthers));
    this.followButtonTarget.classList.toggle("is-current", this.followOthers);
  },

  // A page turn names its board, so a window on another cut or order goes
  // to the same board — or stays put if it does not have it. Its position
  // is only for a board without a name.
  indexFor(data) {
    if (data.slug) return this.indexBySlug.get(data.slug);
    return Number.isInteger(data.index) && this.slideTargets[data.index] ? data.index : undefined;
  },
};
